import { useEffect, useState } from 'react'

import { Badge, Checkbox, Menu, Switch, Tooltip } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import CopyableId from 'src/components/DataDisplay/CopyableId/CopyableId'
import DetailSection from 'src/components/DataDisplay/DetailSection/DetailSection'
import {
  ContactStatus,
  ContactStatusLabels,
  contactSourceLabel,
} from 'src/graphql/types/contacts'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'

import type { ContactTrustTier, RankedCarrierContact } from '../carrier.types'
import { contactLabel } from '../carrier.utils'

import {
  ContactCellInput,
  draftErrors,
  draftHasErrors,
  type ContactDraft,
} from './ContactRowFields'
import ContactUsageBadge from './ContactUsageBadge'

export interface CarrierContactsTableProps {
  contacts: RankedCarrierContact[]
  /** Size of the whole ranked list, not just this page. */
  totalCount: number
  pageSize: number
  /** 1-based, so a reset made by the caller reaches the paginator. */
  currentPage?: number
  onLoadPage: (pageInfo: { offset: number; limit: number }) => void
  /**
   * Archives contacts: out of the running, still on record. Omitted when the
   * caller has no way to edit; given one id by the row menu and the whole
   * selection by the bar.
   */
  onArchiveContacts?: (contactIds: string[]) => void
  /** Which rows are ticked. Held by the caller, which also clears it. */
  selectedIds?: Set<string>
  onSelectedIdsChange?: (next: Set<string>) => void
  /** Whether archived contacts are listed at all. */
  showArchived?: boolean
  onShowArchivedChange?: (next: boolean) => void
  /**
   * Saves the row being edited. Resolves true when the save went through, so
   * the row can stay open and keep the typing when it did not.
   */
  onSaveContact?: (contactId: string, draft: ContactDraft) => Promise<boolean>
  savingContact?: boolean
  /** Opens the history filtered to one contact. */
  onViewHistory?: (contactId: string) => void
  /**
   * Opens the form for a contact the admin learned of directly. Lives in the
   * section header rather than in the table, so it is reachable on a carrier
   * that has no rows yet — the one that needs it most.
   */
  onAddContact?: () => void
}

const SECTION_DESCRIPTION =
  'Ranked the way outreach ranks them: a verified response beats a contact an admin added, which beats a logged contact attempt, which beats the preferred contact, which beats anything else on file. Email and phone are ranked separately, because the same carrier often holds one person’s address and another’s direct line — so the leader for each is its own row. Only active contacts compete — a contact somebody archived is not a contact point, even if it still carries the preferred flag, and it is listed after the ranked ones rather than among them.'

/**
 * What each tier is called, and how it reads.
 *
 * The API sends the tier itself; what it is called on a screen is this
 * screen's to decide, next to the colour it is drawn in. The three confirmed
 * tiers are named for the event that confirmed them. The two unconfirmed ones
 * are the same harvested data with or without a flag, and "on file" says
 * nothing about where a row came from — so those are named for their source
 * instead, and fall back to the tier word only when the source is unknown.
 */
const TIER: Record<ContactTrustTier, { label: string; color: string }> = {
  VOE_RESPONSE: { label: 'Verified response', color: 'green' },
  ADMIN: { label: 'Added by admin', color: 'teal' },
  VOE_AUDIT_TRAIL: { label: 'Contact attempt', color: 'blue' },
  PREFERRED: { label: 'Preferred', color: 'gray' },
  OTHER: { label: 'On file', color: 'gray' },
}

const UNCONFIRMED_TIERS: ReadonlySet<ContactTrustTier> = new Set([
  'PREFERRED',
  'OTHER',
])

const rankingBadge = (
  row: RankedCarrierContact
): { label: string; color: string; tooltip?: string } => {
  const tier = row.tier ?? 'OTHER'
  const fallback = TIER[tier] ?? TIER.OTHER
  if (!UNCONFIRMED_TIERS.has(tier)) return fallback
  const source = contactSourceLabel(row.contact.source)
  return source ? { ...source, color: fallback.color } : fallback
}

const missing = (
  <span className="text-xs italic text-doubleNickel-gray-400">Not on file</span>
)

const CarrierContactsTable = ({
  contacts,
  totalCount,
  pageSize,
  currentPage = 1,
  onLoadPage,
  onArchiveContacts,
  selectedIds = new Set<string>(),
  onSelectedIdsChange,
  showArchived = false,
  onShowArchivedChange,
  onSaveContact,
  savingContact = false,
  onViewHistory,
  onAddContact,
}: CarrierContactsTableProps) => {
  // Edited in place rather than in a modal: the row stays where it is, next to
  // the ranking that explains why it matters.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<ContactDraft>({
    contactName: '',
    email: '',
    phoneNumber: '',
  })
  const [original, setOriginal] = useState<ContactDraft>({
    contactName: '',
    email: '',
    phoneNumber: '',
  })

  const startEdit = (contact: RankedCarrierContact['contact']) => {
    setEditingId(contact.contactId)
    const seeded = {
      contactName: contact.contactName ?? '',
      email: contact.email ?? '',
      // Edited the way it is read, so the number does not change shape on
      // click. The server normalises whatever comes back.
      phoneNumber:
        formatInternationalPhoneNumber(contact.phoneNumber) ??
        contact.phoneNumber ??
        '',
    }
    setDraft(seeded)
    setOriginal(seeded)
  }

  // Judged against what was loaded, not in isolation: a number stored before
  // these rules existed — an extension, a Mexican number — would otherwise
  // disable Save the instant the row opens, blocking an edit to the name.
  const errors = draftErrors(draft, original)
  const isEditing = (row: RankedCarrierContact) =>
    row.contact.contactId === editingId

  // Turning the page takes the edited row off screen, so the edit has to end
  // with it. Left alone, editingId would point at a row that is no longer
  // rendered and the typing would be dropped without a word.
  const changePage = (next: { offset: number; limit: number }) => {
    if (editingId) setEditingId(null)
    onLoadPage(next)
  }

  // A save can merge the edited row away, and a refetch can drop it from the
  // page. Release the id rather than hold a reference to a row that is gone.
  useEffect(() => {
    if (
      editingId &&
      !contacts.some((row) => row.contact.contactId === editingId)
    ) {
      setEditingId(null)
    }
  }, [contacts, editingId])

  // ACTIVE is the only status that competes for a verification, so it is also
  // the only one any of these actions has something to do to.
  const isActive = (row: RankedCarrierContact) =>
    row.contact.status === ContactStatus.ACTIVE

  const selectable = onArchiveContacts && onSelectedIdsChange
  // A contact that is already archived has nothing this action can do to it,
  // so it is shown but never selectable — otherwise the count in the bar would
  // promise more than the call performs.
  const selectableRows = contacts.filter(isActive)
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedIds.has(row.contact.contactId))
  const someSelected = selectableRows.some((row) =>
    selectedIds.has(row.contact.contactId)
  )

  const toggleOne = (contactId: string) => {
    const next = new Set(selectedIds)
    if (next.has(contactId)) next.delete(contactId)
    else next.add(contactId)
    onSelectedIdsChange?.(next)
  }

  // Ticks this page only. It sits in the header of a paginated table, and
  // reaching past what is rendered would arm an action against rows nobody
  // has looked at.
  const toggleAllOnPage = () => {
    const next = new Set(selectedIds)
    for (const row of selectableRows) {
      if (allSelected) next.delete(row.contact.contactId)
      else next.add(row.contact.contactId)
    }
    onSelectedIdsChange?.(next)
  }

  const columns = [
    {
      key: 'select',
      width: 3,
      hidden: !selectable,
      label: (
        <Checkbox
          size="xs"
          aria-label={
            allSelected ? 'Clear selection on this page' : 'Select this page'
          }
          checked={allSelected}
          indeterminate={!allSelected && someSelected}
          disabled={selectableRows.length === 0}
          onChange={toggleAllOnPage}
        />
      ),
      render: (row: RankedCarrierContact) => (
        <Checkbox
          size="xs"
          aria-label={`Select ${contactLabel(row.contact)}`}
          checked={selectedIds.has(row.contact.contactId)}
          disabled={!isActive(row)}
          onChange={() => toggleOne(row.contact.contactId)}
        />
      ),
    },
    {
      key: 'contactId',
      label: 'ID',
      width: 4,
      render: (row: RankedCarrierContact) => (
        // For looking this exact row up in the database.
        <CopyableId value={row.contact.contactId} label="Contact ID" />
      ),
    },
    {
      key: 'contactName',
      label: 'Contact',
      width: 18,
      render: (row: RankedCarrierContact) =>
        isEditing(row) ? (
          <ContactCellInput
            value={draft.contactName}
            onChange={(contactName) =>
              setDraft((current) => ({ ...current, contactName }))
            }
            placeholder="Contact name"
          />
        ) : (
          <span className="text-sm text-doubleNickel-gray-900">
            {row.contact.contactName?.trim() || 'Unnamed contact'}
          </span>
        ),
    },
    {
      key: 'email',
      label: 'Email',
      width: 23,
      render: (row: RankedCarrierContact) =>
        isEditing(row) ? (
          <ContactCellInput
            value={draft.email}
            onChange={(email) => setDraft((current) => ({ ...current, email }))}
            error={errors.email}
            placeholder="Email"
            mono
          />
        ) : row.contact.email?.trim() ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="break-all font-mono text-xs text-doubleNickel-gray-900">
              {row.contact.email}
            </span>
            <ContactUsageBadge
              isUsed={row.isBestEmail}
              label="Used for email"
              explanation="When a verification is created for this carrier without an email of its own, this address is filled in and the request goes here."
            />
          </div>
        ) : (
          missing
        ),
    },
    {
      key: 'phoneNumber',
      label: 'Phone',
      width: 16,
      render: (row: RankedCarrierContact) =>
        isEditing(row) ? (
          <ContactCellInput
            value={draft.phoneNumber}
            onChange={(phoneNumber) =>
              setDraft((current) => ({ ...current, phoneNumber }))
            }
            error={errors.phoneNumber}
            placeholder="Phone"
          />
        ) : row.contact.phoneNumber?.trim() ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-doubleNickel-gray-900">
              {formatInternationalPhoneNumber(row.contact.phoneNumber) ??
                row.contact.phoneNumber}
            </span>
            <ContactUsageBadge
              isUsed={row.isBestPhone}
              label="Used for calls"
              explanation="When a verification is created for this carrier without a phone number of its own, this number is filled in and the call goes here."
            />
          </div>
        ) : (
          missing
        ),
    },
    {
      key: 'tier',
      label: 'Why it ranks',
      width: 14,
      render: (row: RankedCarrierContact) => {
        if (!isActive(row)) {
          // Named by the status it holds rather than by one word for every
          // row that is out of the running: archived and inactive are not the
          // same event, and this column exists to say why a row is where it
          // is.
          return (
            <Badge size="sm" variant="outline" color="gray">
              {ContactStatusLabels[row.contact.status] ?? 'Not in the running'}
            </Badge>
          )
        }
        const { label, color, tooltip } = rankingBadge(row)
        const badge = (
          <Badge size="sm" variant="light" color={color}>
            {label}
          </Badge>
        )
        return tooltip ? (
          <Tooltip label={tooltip} withArrow position="top">
            {badge}
          </Tooltip>
        ) : (
          badge
        )
      },
    },
    {
      key: 'occurrenceCount',
      label: 'Times seen',
      width: 7,
      render: (row: RankedCarrierContact) => (
        <span className="text-xs text-doubleNickel-gray-600">
          {row.contact.occurrenceCount}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 14,
      hidden: !onSaveContact && !onArchiveContacts && !onViewHistory,
      render: (row: RankedCarrierContact) =>
        isEditing(row) ? (
          <div className="flex items-center justify-end gap-1 whitespace-nowrap">
            <DNButton
              variant="transparent"
              className="px-1.5 py-0.5 text-xs"
              onClick={() => setEditingId(null)}
            >
              Cancel
            </DNButton>
            <DNButton
              variant="primary"
              className="px-1.5 py-0.5 text-xs"
              loading={savingContact}
              disabled={draftHasErrors(draft, original)}
              onClick={() => {
                if (draftHasErrors(draft, original)) return
                // The row stays in edit mode until the save succeeds. Closing
                // it here threw the typing away whenever the server refused
                // the value — the one moment it is worth keeping.
                void onSaveContact?.(row.contact.contactId, draft).then(
                  (saved) => {
                    if (saved) setEditingId(null)
                  }
                )
              }}
            >
              Save
            </DNButton>
          </div>
        ) : (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <DNButton
                variant="transparent"
                className="px-2 py-0.5 text-xs"
                aria-label={`Actions for ${
                  row.contact.contactName?.trim() || 'this contact'
                }`}
              >
                •••
              </DNButton>
            </Menu.Target>
            <Menu.Dropdown>
              {onSaveContact && isActive(row) && (
                <Menu.Item onClick={() => startEdit(row.contact)}>
                  Edit
                </Menu.Item>
              )}
              {onViewHistory && (
                <Menu.Item onClick={() => onViewHistory(row.contact.contactId)}>
                  View history
                </Menu.Item>
              )}
              {onArchiveContacts && isActive(row) && (
                <Menu.Item
                  color="red"
                  onClick={() => onArchiveContacts([row.contact.contactId])}
                >
                  Archive
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        ),
    },
  ]

  const selectedCount = selectedIds.size

  return (
    <DetailSection
      // Counted in the heading because the table is paginated: the rows on
      // screen are not the size of the list, and this is the number somebody
      // archiving contacts is watching.
      title={`Contacts (${totalCount})`}
      description={SECTION_DESCRIPTION}
      columns={1}
      actions={
        (onShowArchivedChange || onAddContact) && (
          <>
            {onShowArchivedChange && (
              <Switch
                size="sm"
                label="Show archived"
                checked={showArchived}
                onChange={(event) =>
                  onShowArchivedChange(event.currentTarget.checked)
                }
              />
            )}
            {onAddContact && (
              <DNButton
                variant="primary"
                className="px-2.5 py-1 text-xs"
                onClick={onAddContact}
              >
                Add contact
              </DNButton>
            )}
          </>
        )
      }
    >
      {selectable && selectedCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-gray-25 px-3 py-2">
          <span className="text-sm text-doubleNickel-gray-700">
            {selectedCount} contact{selectedCount === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-1">
            <DNButton
              variant="transparent"
              className="px-2 py-1 text-xs"
              onClick={() => onSelectedIdsChange?.(new Set())}
            >
              Clear
            </DNButton>
            <DNButton
              variant="error"
              className="px-2 py-1 text-xs"
              onClick={() => onArchiveContacts?.([...selectedIds])}
            >
              Archive
            </DNButton>
          </div>
        </div>
      )}

      {contacts.length === 0 && totalCount === 0 ? (
        <div className="py-2 text-sm text-doubleNickel-warning-700">
          No contacts on file. A verification for this carrier has nowhere to go
          except the carrier-level details above.
        </div>
      ) : (
        // Rendered even with an empty page, as long as the list is not empty:
        // archiving the last rows of a page leaves the reader past the end of
        // it for a moment, and "no contacts on file" is the wrong thing to say
        // about a carrier that has them.
        <DnTable
          columns={columns}
          data={contacts}
          totalCount={totalCount}
          defaultResultsPerPage={pageSize}
          paginationProps={{ initialPage: currentPage }}
          onLoadPage={changePage}
        />
      )}
    </DetailSection>
  )
}

export default CarrierContactsTable
